// import chevironRight from "$/cheviron-right.svg";
import { createFileRoute } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { useState, useEffect, useRef } from "react";
import { BottomArrow } from "@/Icon/BottomArrow";
import { Divider } from "@/Divider";
// import ToggleNoTextSwitch from "@/ToggleNonTextSwitch";
import circleSmall from "$/circle-small.svg";
import { Button } from "@/Button";
import { Loading } from "@/Loading";
import { useCoinProductList } from "../../../hooks/payment/useCoin";
import type { CoinProduct } from "../../../api/types/payment";
import { createCoinPreOrder } from "../../../api/services/payment/coin";
import { useToastStore } from "&/toast";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";

export const Route = createFileRoute("/customer/layout/user-buy-coin")({
  component: User,
});

function User() {
  const { data: coinProductData } = useCoinProductList();
  const { add } = useToastStore();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedSinglePurchase, setSelectedSinglePurchase] =
    useState<CoinProduct | null>(null);
  const [selectedAutoPayment, setSelectedAutoPayment] =
    useState<CoinProduct | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [showPaymentWidget, setShowPaymentWidget] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);

  const tossPaymentsRef = useRef<any>(null);
  const widgetsRef = useRef<any>(null);
  const paymentMethodWidgetRef = useRef<any>(null);
  const agreementWidgetRef = useRef<any>(null);

  // 토스페이먼츠 초기화 (공식 로더 사용)
  useEffect(() => {
    let isMounted = true;
    const clientKey =
      import.meta.env.VITE_TOSS_CLIENT_KEY ||
      "test_gck_docs_Ovk5rk1EwkEbP0W43n07xlzm";
    const redirectUrl =
      import.meta.env.VITE_TOSS_REDIRECT_URL ||
      "https://app.mungtoday.com/api/payment/toss/brandpay/auth";

    const initializeTossPayments = async () => {
      try {
        const toss = await loadTossPayments(clientKey, redirectUrl);
        if (!isMounted) return;
        tossPaymentsRef.current = toss;
        console.log("토스페이먼츠 초기화 완료");
      } catch (err: any) {
        console.error("토스페이먼츠 초기화 실패:", err);
        add("결제 시스템 초기화에 실패했습니다.");
      }
    };

    initializeTossPayments();

    return () => {
      isMounted = false;
    };
  }, [add]);

  // 결제 위젯 렌더링
  useEffect(() => {
    if (!showPaymentWidget || !tossPaymentsRef.current || !orderData) return;

    const renderPaymentWidget = async () => {
      const redirectUrl =
        import.meta.env.VITE_TOSS_REDIRECT_URL ||
        "https://app.mungtoday.com/api/payment/toss/brandpay/auth";
      try {
        // 기존 위젯이 있다면 제거
        if (paymentMethodWidgetRef.current) {
          await paymentMethodWidgetRef.current.destroy();
        }
        if (agreementWidgetRef.current) {
          await agreementWidgetRef.current.destroy();
        }

        // 위젯 인스턴스 생성
        widgetsRef.current = tossPaymentsRef.current.widgets({
          customerKey: orderData.customerKey || "CUSTOMER_KEY",
          brandpay: {
            redirectUrl: redirectUrl,
          },
        });

        // 결제 금액 설정
        await widgetsRef.current.setAmount({
          value: orderData.costAmount,
          currency: "KRW",
        });

        // 결제 수단 UI 렌더링
        const paymentMethodWidget =
          await widgetsRef.current.renderPaymentMethods({
            selector: "#payment-methods",
            variantKey: "DEFAULT",
          });

        // 약관 UI 렌더링
        const agreementWidget = await widgetsRef.current.renderAgreement({
          selector: "#agreement",
        });

        // 위젯 인스턴스 저장
        paymentMethodWidgetRef.current = paymentMethodWidget;
        agreementWidgetRef.current = agreementWidget;

        // 결제 수단 선택 이벤트
        paymentMethodWidget.on(
          "paymentMethodSelect",
          (selectedPaymentMethod: any) => {
            console.log("선택된 결제수단:", selectedPaymentMethod);
          }
        );

        setIsPaymentProcessing(false);
      } catch (error) {
        console.error("결제 위젯 렌더링 실패:", error);
        add("결제 위젯을 불러오는데 실패했습니다.");
        setIsPaymentProcessing(false);
      }
    };

    renderPaymentWidget();
  }, [showPaymentWidget, orderData, add]);

  // 가격 계산 함수 (quantity * 100원 기준)
  const calculatePrice = (quantity: number) => {
    return quantity * 100;
  };

  // // 결제 검증 API 호출
  // const verifyPayment = async (
  //   paymentData: PaymentVerificationRequest
  // ): Promise<PaymentVerificationResponse> => {
  //   const response = await fetch("/api/payment/verify", {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //     },
  //     body: JSON.stringify(paymentData),
  //   });

  //   if (!response.ok) {
  //     throw new Error("결제 검증에 실패했습니다.");
  //   }

  //   return response.json();
  // };

  // 결제 요청 처리
  const handlePaymentRequest = async () => {
    if (!widgetsRef.current || !orderData) {
      add("결제 시스템이 준비되지 않았습니다.");
      return;
    }

    try {
      setIsPaymentProcessing(true);

      const paymentResult = await widgetsRef.current.requestPayment({
        orderId: orderData.orderId,
        orderName: `멍코인 ${selectedSinglePurchase?.quantity}개`,
        customerName: orderData.customerName || "고객",
        customerEmail: orderData.customerEmail || "",
        successUrl: `${window.location.origin}/customer/layout/payment/success`,
        failUrl: `${window.location.origin}/customer/layout/payment/fail`,
      });

      console.log("결제 요청 성공:", paymentResult);
    } catch (error: any) {
      console.error("결제 요청 실패:", error);

      if (error.code) {
        switch (error.code) {
          case "PAY_PROCESS_CANCELED":
            add("결제가 취소되었습니다.");
            break;
          case "PAY_PROCESS_ABORTED":
            add("결제가 중단되었습니다.");
            break;
          default:
            add(error.message || "결제에 실패했습니다.");
        }
      } else {
        add("결제에 실패했습니다.");
      }
      setIsPaymentProcessing(false);
    }
  };

  // 코인 구매 처리 (결제 위젯 방식)
  const handleCoinPurchase = async () => {
    if (!selectedSinglePurchase) {
      add("구매할 코인을 선택해주세요.");
      return;
    }

    if (!tossPaymentsRef.current) {
      add("결제 시스템을 초기화할 수 없습니다.");
      return;
    }

    setIsLoading(true);

    try {
      // 주문 생성
      const request = {
        coinProductSeq: selectedSinglePurchase.coinProductSeq,
        isRecurringYn: "N",
      };

      const response = await createCoinPreOrder(request);
      console.log("주문 생성 응답:", response);

      setOrderData(response.data);
      setShowPaymentWidget(true);
    } catch (error: any) {
      console.error("코인 구매 실패:", error);
      add("코인 구매에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 결제 위젯 닫기
  const handleClosePaymentWidget = () => {
    setShowPaymentWidget(false);
    setOrderData(null);
  };

  // 데이터 로딩 완료 후 초기값 설정
  if (
    coinProductData &&
    coinProductData.data.length > 0 &&
    !selectedSinglePurchase &&
    !selectedAutoPayment
  ) {
    setSelectedSinglePurchase(coinProductData.data[0]);
    setSelectedAutoPayment(coinProductData.data[0]);
  }

  // 결제 위젯이 표시되는 경우
  if (showPaymentWidget) {
    return (
      <>
        <LayoutHeader title={"결제"} hasBackBtn hasFootPrint={false} />
        <div className="flex flex-col flex-auto bg-[#F5F5F5]">
          <div className="px-4 pt-8 pb-8 mx-auto max-w-md">
            {/* 결제 정보 */}
            <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
              <h1 className="mb-4 text-xl font-bold text-center">결제 정보</h1>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">주문번호</span>
                  <span className="font-medium">{orderData?.orderId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">결제금액</span>
                  <span className="font-medium text-orange-400">
                    {orderData?.costAmount?.toLocaleString()}원
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">구매수량</span>
                  <span className="font-medium">
                    {orderData?.purchaseQuantity}개
                  </span>
                </div>
              </div>
            </div>

            {/* 토스페이먼츠 결제 위젯 */}
            <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">결제 수단 선택</h2>
              <div id="payment-methods" className="mb-6"></div>
            </div>

            <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">이용약관</h2>
              <div id="agreement" className="mb-6"></div>
            </div>

            {/* 결제 버튼 */}
            <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
              <button
                className="px-6 py-4 mb-3 w-full text-lg font-medium text-white bg-orange-400 rounded-lg"
                onClick={handlePaymentRequest}
                disabled={isPaymentProcessing}
              >
                {isPaymentProcessing ? "결제 중..." : "결제하기"}
              </button>
              <button
                className="px-6 py-3 w-full font-medium text-gray-600 rounded-lg border border-gray-300"
                onClick={handleClosePaymentWidget}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader title={"멍코인 구매"} hasBackBtn hasFootPrint={false} />
      <div className="flex flex-col flex-auto bg-[#F5F5F5]">
        <div className="flex flex-col flex-auto gap-2 px-5 pt-5 rounded-t-3xl">
          {/* 멍코인 구매 섹션 */}
          <div className="">
            <div className="text-[15px] font-semibold">멍코인 구매</div>
            {/* 일반 구매 */}
            <div className="flex justify-between my-2 w-full">
              <div className="flex relative items-center">
                {/* 왼쪽 드롭다운 - 고정 */}
                <div
                  className="flex flex-shrink-0 items-center mr-4 w-24 h-10 bg-white rounded-md border border-gray-400 select-none"
                  onClick={() => setDropdownOpen((v) => !v)}
                >
                  <span className="mx-2 text-sm font-medium w-[45px] text-center">
                    {selectedSinglePurchase?.quantity || 0}
                  </span>
                  <div className="flex justify-center items-center mr-2 w-4 h-4">
                    <BottomArrow />
                  </div>
                </div>
                {/* 드롭다운 메뉴 */}
                {dropdownOpen && (
                  <div className="absolute top-11 z-10 w-24 bg-white rounded border shadow">
                    {coinProductData?.data?.map(
                      (product: CoinProduct, index: number) => (
                        <div
                          key={index}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-orange-50
                      ${product.quantity === selectedSinglePurchase?.quantity ? "text-orange-500 font-bold" : ""}`}
                          onClick={() => {
                            setSelectedSinglePurchase(product);
                            setDropdownOpen(false);
                          }}
                        >
                          {product.quantity}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {selectedSinglePurchase
                  ? calculatePrice(
                      selectedSinglePurchase.quantity
                    ).toLocaleString()
                  : 0}{" "}
                원
              </div>
            </div>
            <Divider line className="my-4" />
          </div>

          {/* 부가서비스 선택 섹션 */}
          <div className="space-y-4">
            <div className="text-[15px] font-semibold">부가서비스 선택</div>
            <div className="space-y-3 text-sm text-[#666666]">
              <div className="flex gap-1 items-start">
                <div className="flex-shrink-0 mt-2 w-3 h-3 bg-gray-400 rounded-full">
                  <img src={circleSmall} alt="circle" />
                </div>
                <p>
                  멍코인은 멍투데이 서비스에서 이용 가능한 화폐단위로, 1멍코인당
                  10원 상당의 가치를 가집니다.
                </p>
              </div>
              <div className="flex gap-1 items-start">
                <div className="flex-shrink-0 mt-2 w-3 h-3 bg-gray-400 rounded-full">
                  <img src={circleSmall} alt="circle" />
                </div>
                <p>
                  모든 서비스는 1멍코인 단위로 각 위탁 사업자의 요금에 맞춰
                  서비스를 이용하실 수 있습니다.
                </p>
              </div>
              <div className="flex gap-1 items-start">
                <div className="flex-shrink-0 mt-2 w-3 h-3 bg-gray-400 rounded-full">
                  <img src={circleSmall} alt="circle" />
                </div>
                <p>
                  멍코인의 유효기간은 5년이며, 유효기간 내 서비스 없은 잔여
                  멍코인은 자동으로 소멸됩니다.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-center px-5 mt-8 mb-8">
          <Button
            className="w-full"
            onClick={handleCoinPurchase}
            disabled={isLoading || isPaymentProcessing}
          >
            {isLoading || isPaymentProcessing ? (
              <Loading />
            ) : (
              <div className="py-3 w-full text-center text-white bg-orange-400 rounded-lg">
                <p className="text-base font-semibold">구매하기</p>
              </div>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
